import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import type InlineVideo from '@helios/shared/types/inline-video'

interface Props extends React.VideoHTMLAttributes<HTMLVideoElement> {
    video: InlineVideo
    posterWidth?: number
    posterHeight?: number
    dimensions?: {
        width: number
        height: number
    }
}

export const Video = React.forwardRef<HTMLVideoElement, Props>(
    ({video, poster, posterWidth, posterHeight, dimensions, ...props}, ref) => {
        const [videoContainerRef, inView] = useInView({
            threshold: 0,
            triggerOnce: true,
        })
        let posterSrc = ''
        let posterPlaceholder = ''

        if (poster) {
            const [canonical, defaultParams] = poster.split('?')
            const params = new URLSearchParams(defaultParams)

            if (!canonical.startsWith('data:image') && !canonical.endsWith('.svg')) {
                params.append('dpr', '2')

                if (posterWidth) {
                    params.append('w', posterWidth.toString())
                }

                if (posterHeight) {
                    params.append('h', posterHeight.toString())
                }
            }

            posterSrc = `${canonical}?${params.toString()}`

            // If we get the poster image dimensions from graphql we create a placeholder svg until the video is inView
            if (dimensions?.width && dimensions?.height) {
                posterPlaceholder = `data:image/svg+xml;charset=utf-8,<svg width='${dimensions.width}px' height='${dimensions.height}px' xmlns='http://www.w3.org/2000/svg'></svg>`
            } else {
                posterPlaceholder = posterSrc
            }
        }

        return (
            <div ref={videoContainerRef}>
                {inView ? (
                    <StyledVideo
                        poster={posterSrc}
                        ref={ref}
                        loop
                        autoPlay
                        playsInline
                        muted
                        disableRemotePlayback
                        {...props}
                    >
                        {video.webm && <source key="webm" src={video.webm} type="video/webm" />}
                        {video.mp4 && <source key="mp4" src={video.mp4} type="video/mp4" />}
                    </StyledVideo>
                ) : (
                    <img src={posterPlaceholder} alt="" />
                )}
            </div>
        )
    }
)

const StyledVideo = styled.video`
    display: block;
    width: 100%;
    max-width: 100%;
    object-fit: inherit;
`
